<script>
import { questionTypeLabel } from '@/constants/question_types';

import { QUESTION_SECTIONS } from '@/constants/question_sections';

const CHOICE_TYPES = ['RADIO', 'SELECT', 'MULTI_SELECT', 'CHECKBOX'];

export default {
  name: 'QuestionPanel',
  props: {
    /** null when creating. */
    question: { type: Object, default: null },
    /** Pre-selected section when opened from a section header. */
    defaultSection: { type: Number, default: -1 },
    typesList: { type: Array, default: () => [] },
    typesLookup: { type: Object, default: () => ({}) },
    saving: { type: Boolean, default: false },
  },
  emits: ['save', 'close'],
  data() {
    const q = this.question;

    return {
      label: q?.label ?? '',
      type: q?.type ?? this.typesList[0] ?? 'SHORT_TEXT',
      step: q ? (q.step ?? -1) : this.defaultSection,
      isRequired: q?.isRequired ?? false,
      isVisible: q?.isVisible ?? true,
      options: (q?.options ?? []).map((o) => ({ ...o })),
      error: null,
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.question?.id);
    },

    title() {
      return this.isEdit ? 'Modifier la question' : 'Nouvelle question';
    },

    sections() {
      return [
        { value: -1, label: 'Non assignée' },
        ...QUESTION_SECTIONS.map((section, index) => ({
          value: index,
          label: section.name,
        })),
      ];
    },

    hasOptions() {
      const key = String(this.type || '').toUpperCase();
      const meta = this.typesLookup?.[key];

      if (meta && typeof meta.needsOptions === 'boolean') {
        return meta.needsOptions;
      }

      return CHOICE_TYPES.includes(key);
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    this.$refs.label?.focus();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') {
        this.$emit('close');
      }
    },

    typeLabel(key) {
      return questionTypeLabel(key, this.typesLookup);
    },

    addOption() {
      this.options.push({ label: '', value: '' });
    },

    removeOption(index) {
      this.options.splice(index, 1);
    },

    updateOption(index, value) {
      this.options.splice(index, 1, {
        ...this.options[index],
        label: value,
        value,
      });
    },

    save() {
      const label = this.label.trim();

      if (!label) {
        this.error = 'Le libellé est requis.';
        return;
      }
      if (!this.type) {
        this.error = 'Le type de réponse est requis.';
        return;
      }

      const options = this.hasOptions
        ? this.options
            .map((o, i) => ({ ...o, label: o.label?.trim(), order: i }))
            .filter((o) => o.label)
        : [];

      if (this.hasOptions && !options.length) {
        this.error = 'Ce type de réponse demande au moins une option.';
        return;
      }

      this.error = null;

      this.$emit('save', {
        ...(this.question ? { id: this.question.id } : {}),
        label,
        type: String(this.type).toUpperCase(),
        step: this.step,
        isRequired: this.isRequired,
        isVisible: this.isVisible,
        options,
      });
    },
  },
};
</script>

<template>
  <div class="q-panel">
    <div class="q-panel__scrim" @click="$emit('close')"></div>

    <aside
      class="q-panel__sheet"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
    >
      <h2 class="q-panel__title">{{ title }}</h2>

      <p v-if="error" class="q-panel__error" role="alert">{{ error }}</p>

      <label class="q-panel__label" for="q-label">Libellé de la question</label>
      <textarea
        id="q-label"
        ref="label"
        v-model="label"
        class="list-input q-panel__textarea"
        rows="2"
        placeholder="Libellé de la question"
      ></textarea>

      <label class="q-panel__label" for="q-type">Type de réponse</label>
      <select id="q-type" v-model="type" class="list-select q-panel__control">
        <option v-for="t in typesList" :key="t" :value="t">
          {{ typeLabel(t) }}
        </option>
      </select>

      <label class="q-panel__label" for="q-section">Section</label>
      <select
        id="q-section"
        v-model.number="step"
        class="list-select q-panel__control"
      >
        <option v-for="s in sections" :key="s.value" :value="s.value">
          {{ s.label }}
        </option>
      </select>

      <div v-if="hasOptions" class="q-panel__options">
        <span class="q-panel__label">Options de réponse</span>

        <div
          v-for="(option, index) in options"
          :key="index"
          class="q-panel__option"
        >
          <input
            class="list-input"
            placeholder="Libellé de l'option"
            :value="option.label"
            @input="updateOption(index, $event.target.value)"
          />
          <button
            type="button"
            class="row-action row-action--danger"
            title="Retirer l'option"
            @click="removeOption(index)"
          >
            <i class="ri-close-line"></i>
          </button>
        </div>

        <button type="button" class="q-panel__add-option" @click="addOption">
          Ajouter une option
        </button>
      </div>

      <div class="q-panel__flags">
        <label class="q-panel__flag">
          <input v-model="isRequired" type="checkbox" />
          Question obligatoire
        </label>
        <label class="q-panel__flag">
          <input v-model="isVisible" type="checkbox" />
          Visible dans l'enquête
        </label>
      </div>

      <div class="q-panel__actions">
        <button
          type="button"
          class="list-btn list-btn--ghost"
          @click="$emit('close')"
        >
          Annuler
        </button>
        <button
          type="button"
          class="list-btn list-btn--primary"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
        </button>
      </div>
    </aside>
  </div>
</template>

<style scoped>
.q-panel {
  position: fixed;
  inset: 0;
  z-index: 1060;
}

.q-panel__scrim {
  position: absolute;
  inset: 0;
  background: rgba(33, 37, 41, 0.35);
}

.q-panel__sheet {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 100%;
  background: #fff;
  border-left: 1px solid #e9ebec;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.q-panel__title {
  margin: 0 0 20px;
  font-size: 16px;
  font-weight: 500;
  color: #212529;
}

.q-panel__error {
  margin: 0 0 16px;
  background: #fdeaec;
  border: 1px solid #f5c2c7;
  border-radius: 6px;
  color: #dc3545;
  font-size: 13px;
  padding: 10px 12px;
}

.q-panel__label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.q-panel__textarea {
  height: auto;
  padding: 10px 12px;
  resize: vertical;
  margin-bottom: 16px;
  font-family: inherit;
}

.q-panel__control {
  width: 100%;
  margin-bottom: 16px;
}

.q-panel__options {
  border-top: 1px solid #f1f3f6;
  padding-top: 16px;
  margin-bottom: 16px;
}

.q-panel__option {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.q-panel__add-option {
  border: 0;
  background: none;
  padding: 0;
  font-size: 13px;
  color: #005bd0;
  cursor: pointer;
}

.q-panel__add-option:hover {
  text-decoration: underline;
}

.q-panel__flags {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-top: 1px solid #f1f3f6;
  padding-top: 16px;
}

.q-panel__flag {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
}

.q-panel__flag input {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: #005bd0;
  cursor: pointer;
}

.q-panel__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: auto;
  padding-top: 24px;
}
</style>
